import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { UndefinedOption } from '@df/ui/atomic/atomic-options';
import { IResizeOptions } from '@df/ui/common/resize.service';
import { PhotoswipeOptions } from '@df/ui/photoswipe/photoswipe.types';
import { NICmsContentField } from '@icms/core/content';
import last from 'lodash-es/last';
import { defaultImage } from '../../icms.config';

export namespace NImageVideo {
    export interface IData {
        image?: string;
        video?: string;
        videoProvider?: EVideoProviders;
    }
    export enum EVideoProviders {
        vimeo = 'vimeo',
        youtube = 'youtube'
    }
    export const VideoProviderOptions = [
        ...UndefinedOption,
        {
            key: EVideoProviders.youtube,
            title: 'YouTube'
        },
        {
            key: EVideoProviders.vimeo,
            title: 'Vimeo'
        }
    ];
    export const CMS_FIELDS = [
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: defaultImage,
            canShow: data => !data?.videoProvider,
            required: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'videoProvider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Video Provider',
            options: NImageVideo.VideoProviderOptions
        },
        {
            key: 'video',
            type: NICmsContentField.EFieldType.text,
            title: 'Video ID or URL',
            canShow: data => !!data?.videoProvider,
            required: true
        }
    ];
}

/**
 * Compnent that presents image or video
 */
@Component({
    selector: 'image-video',
    templateUrl: './image-video.html',
    styleUrls: ['./image-video.scss', './has-video-zoom.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageVideoComponent {
    /**
     * Source item
     */
    @Input()
    source: NImageVideo.IData = {};

    /**
     * Image alt attribute
     */
    @Input()
    alt?: string;

    @Input()
    photoswipeOption?: PhotoswipeOptions;

    /**
     * Autoplay video
     */
    @Input()
    autoplay = true;
    /**
     * Youtube / Vimaeo video
     */
    @Input()
    provider?: NImageVideo.EVideoProviders;

    @Input()
    options?: IResizeOptions;

    @Input({ required: true })
    ratio!: string | number;

    EVideoProviders = NImageVideo.EVideoProviders;

    constructor(private changeDetectoRef: ChangeDetectorRef) {}

    get hasImageZoom(): boolean {
        return !!(this.photoswipeOption && !this.source.video && this.source.image);
    }

    get hasVideoZoom(): boolean {
        return !!(this.photoswipeOption && this.source.video);
    }

    get photoswipeClass() {
        return this.photoswipeOption ? `photoswipe-index-${this.photoswipeOption.galleryUID}-${this.photoswipeOption.index}` : '';
    }

    get shouldRenderVideo(): boolean {
        return !!this.source.video && !this.photoswipeOption;
    }

    get photoswipeImage() {
        return this.photoswipeOption && this.source.image;
    }

    get photoswipeUid(): string | undefined {
        return this.photoswipeOption && (this.source.image || this.source.video) + '.' + this.photoswipeOption.index;
    }

    get videoIframeHtml(): string | undefined {
        return this.source?.video && this.photoswipeOption
            ? `<div  style="bottom:7em; left:0; right:0; top:5em; position:absolute;" class="flex flex-justify-center"><div class="ratio-16-9 w-12" >
        <iframe class="fill" src="${this.provider === NImageVideo.EVideoProviders.youtube ? this.youtubeIframeSource : this.vimeoIframeSource}"></iframe></div></div>`
            : undefined;
    }

    private get youtubeIframeSource(): string {
        return `https://www.youtube.com/embed/${this.source.video}?autoplay=${this.autoplay ? 'true' : 'false'}&loop=1&modestbranding=1&showinfo=0&controls=0`;
    }

    private get vimeoIframeSource(): string {
        const id = last(this.source.video?.split('/'));
        return `https://player.vimeo.com/video/${id}?${this.autoplay ? 'background=1' : 'controls=1'}&responsive=1`;
    }
}
